// src/components/admin/TenantManagement.jsx
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiUsers, FiCalendar, FiDollarSign, FiHome, FiSettings, FiEdit2 } from 'react-icons/fi';
import { tenantService } from '../../services/tenantService';
import toast from 'react-hot-toast';
import ConfirmationModal from '../common/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';

const TenantManagement = () => {
  const { confirmationState, showConfirmation, handleConfirm, handleCancel } = useConfirmation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const { currentTenant, switchTenant } = useTenant();
  const { userProfile } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postcode: '',
      country: 'United Kingdom'
    },
    branding: {
      primaryColor: '#2563eb',
      secondaryColor: '#1e40af'
    }
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const tenantsData = await tenantService.getAllTenants();
      setTenants(tenantsData.filter(t => t.status === 'active'));
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error('Failed to load clinics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    
    try {
      // Generate slug from name if not provided
      if (!formData.slug) {
        formData.slug = tenantService.generateSlug(formData.name);
      }

      // Check if slug is available
      const isAvailable = await tenantService.isSlugAvailable(formData.slug);
      if (!isAvailable) {
        toast.error('Clinic URL slug is already taken');
        return;
      }

      const newTenant = await tenantService.createTenant(formData);
      setTenants([...tenants, newTenant]);
      setShowCreateModal(false);
      resetForm();
      toast.success(`Clinic "${newTenant.name}" created successfully`);
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error('Failed to create clinic');
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    
    try {
      const updatedTenant = await tenantService.updateTenant(editingTenant.id, formData);
      setTenants(tenants.map(t => t.id === editingTenant.id ? updatedTenant : t));
      setEditingTenant(null);
      resetForm();
      toast.success(`Clinic "${updatedTenant.name}" updated successfully`);
    } catch (error) {
      console.error('Error updating tenant:', error);
      toast.error('Failed to update clinic');
    }
  };

  const handleDeleteTenant = async (tenant) => {
    const confirmed = await showConfirmation({
      title: 'Delete Clinic',
      message: `Are you sure you want to delete "${tenant.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;

    try {
      await tenantService.deleteTenant(tenant.id);
      setTenants(tenants.filter(t => t.id !== tenant.id));
      toast.success(`Clinic "${tenant.name}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error('Failed to delete clinic');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        postcode: '',
        country: 'United Kingdom'
      },
      branding: {
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af'
      }
    });
  };

  const openEditModal = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name || '',
      slug: tenant.slug || '',
      description: tenant.description || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      address: tenant.address || {
        street: '',
        city: '',
        postcode: '',
        country: 'United Kingdom'
      },
      branding: tenant.branding || {
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af'
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Only super admins can manage tenants
  if (userProfile?.role !== 'super_admin') {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <FiHome className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            Only super administrators can manage clinics.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clinic Management</h2>
          <p className="text-gray-600">Manage multiple clinic locations and settings</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus className="h-4 w-4" />
          Add Clinic
        </button>
      </div>

      {/* Current Tenant Info */}
      {currentTenant && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <FiHome className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="font-medium text-blue-900">Currently Managing</h3>
              <p className="text-blue-700">{currentTenant.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tenants.map((tenant) => (
          <div key={tenant.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{tenant.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{tenant.description}</p>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">URL:</span> /{tenant.slug}
                  </p>
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Email:</span> {tenant.email}
                  </p>
                  {tenant.phone && (
                    <p className="text-xs text-gray-500">
                      <span className="font-medium">Phone:</span> {tenant.phone}
                    </p>
                  )}
                </div>
              </div>
              <div 
                className="w-4 h-4 rounded-full border-2 border-gray-300"
                style={{ backgroundColor: tenant.branding?.primaryColor || '#2563eb' }}
              />
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <FiUsers className="h-3 w-3" />
                  Active
                </span>
                <span className="flex items-center gap-1">
                  <FiSettings className="h-3 w-3" />
                  {tenant.settings?.treatmentTypes?.length || 0} treatments
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {currentTenant?.id !== tenant.id && (
                  <button
                    onClick={() => switchTenant(tenant.id)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Switch
                  </button>
                )}
                <button
                  onClick={() => openEditModal(tenant)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiEdit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteTenant(tenant)}
                  className="text-red-400 hover:text-red-600"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTenant) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingTenant ? 'Edit Clinic' : 'Create New Clinic'}
            </h3>
            
            <form onSubmit={editingTenant ? handleUpdateTenant : handleCreateTenant} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clinic Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Slug *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    placeholder="clinic-name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Address</h4>
                <div>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    placeholder="Street Address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    name="address.postcode"
                    value={formData.address.postcode}
                    onChange={handleInputChange}
                    placeholder="Postcode"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Branding */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Branding</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Color
                    </label>
                    <input
                      type="color"
                      name="branding.primaryColor"
                      value={formData.branding.primaryColor}
                      onChange={handleInputChange}
                      className="w-full h-10 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secondary Color
                    </label>
                    <input
                      type="color"
                      name="branding.secondaryColor"
                      value={formData.branding.secondaryColor}
                      onChange={handleInputChange}
                      className="w-full h-10 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTenant(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingTenant ? 'Update Clinic' : 'Create Clinic'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />
    </div>
  );
};

export default TenantManagement;
